import { token } from '@/infrastructure/container';
import { type ILogger } from './Logger';

/**
 * Privacy-light product analytics: a handful of named events, no PII, no book content. The real
 * implementation POSTs to a same-origin Cloudflare Pages Function (`/api/event`) that records to
 * Workers Analytics Engine. Tests use the in-memory doubles below.
 */
export interface IAnalytics {
  /** A book was successfully parsed and opened. */
  bookAdded(format: string): void;
  /** Playback started — begins timing a listening segment. */
  listenStarted(): void;
  /** Playback stopped (pause, end of book, error) — reports the segment's duration and format. */
  listenStopped(format?: string): void;
  /** Something went wrong, so we can spot bugs in the wild. */
  error(message: string): void;
}

export const TOKEN_IAnalytics = token<IAnalytics>('IAnalytics');

const ENDPOINT = '/api/event';
const MAX_MESSAGE = 300;

interface EventPayload {
  event: 'book_added' | 'listen' | 'error';
  format?: string;
  seconds?: number;
  message?: string;
}

export class Analytics implements IAnalytics {
  private listenStartedAt: number | null = null;
  // Guards against the error -> send -> (failed fetch) -> unhandledrejection -> error … loop.
  private reportingError = false;

  constructor(private logger: ILogger) {
    // Catch otherwise-invisible runtime errors so "any bugs in the wild" is actually answerable.
    window.addEventListener('error', (e) => this.error(e.message));
    window.addEventListener('unhandledrejection', (e) => this.error(reasonMessage(e.reason)));
  }

  bookAdded(format: string): void {
    this.send({ event: 'book_added', format });
  }

  listenStarted(): void {
    // Idempotent: a second start without a stop keeps the original segment rather than dropping it.
    if (this.listenStartedAt === null) this.listenStartedAt = performance.now();
  }

  listenStopped(format?: string): void {
    if (this.listenStartedAt === null) return;
    const seconds = Math.round((performance.now() - this.listenStartedAt) / 1000);
    this.listenStartedAt = null;
    if (seconds > 0) this.send({ event: 'listen', seconds, format });
  }

  error(message: string): void {
    if (this.reportingError) return; // never report an error caused by reporting an error
    this.reportingError = true;
    try {
      this.send({ event: 'error', message: message.slice(0, MAX_MESSAGE) });
    } finally {
      this.reportingError = false;
    }
  }

  private send(payload: EventPayload): void {
    try {
      const body = JSON.stringify(payload);
      if (navigator.sendBeacon) {
        navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
      } else {
        // .catch is essential: an un-caught rejection here would re-trigger the error handler.
        void fetch(ENDPOINT, {
          method: 'POST',
          body,
          keepalive: true,
          headers: { 'content-type': 'application/json' },
        }).catch(() => {});
      }
    } catch (error) {
      // Analytics must never break the app.
      this.logger.warn('Analytics send failed', error);
    }
  }
}

/** Pull a short, object-free message off a rejection reason (avoid leaking stacks / payloads). */
function reasonMessage(reason: unknown): string {
  if (reason instanceof Error) return reason.message;
  if (typeof reason === 'string') return reason;
  return 'unhandledrejection';
}

/** No-op double: the default in tests, and a clean fallback if analytics is disabled. */
export class NoopAnalytics implements IAnalytics {
  bookAdded(): void {}
  listenStarted(): void {}
  listenStopped(): void {}
  error(): void {}
}

/** Recording double for asserting which events a store emits. */
export class RecordingAnalytics implements IAnalytics {
  readonly events: { type: string; value?: string }[] = [];
  bookAdded(format: string): void {
    this.events.push({ type: 'bookAdded', value: format });
  }
  listenStarted(): void {
    this.events.push({ type: 'listenStarted' });
  }
  listenStopped(format?: string): void {
    this.events.push({ type: 'listenStopped', value: format });
  }
  error(message: string): void {
    this.events.push({ type: 'error', value: message });
  }
}
